import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { ENDPOINT } from '../utils/Endpoints';

interface AuthContextType {
  isAuthenticated: boolean;
  authLoading: boolean;
  userClaims: string[];
  token: string | null;
  userId: string | null;
  login: (email: string, token: string, claim: string, userId?: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading]         = useState(true);  // true until localStorage is checked
  const [userClaims, setUserClaims] = useState<string[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    // Check if the user is authenticated by reading from localStorage
    const email = localStorage.getItem('email');
    const storedClaims = localStorage.getItem('defaultClaim');
    const storedToken = localStorage.getItem('token');
    const storedUserId = localStorage.getItem('userId');

    if (email && storedClaims && storedToken) {
      setIsAuthenticated(true);
      setUserClaims([storedClaims]);
      setToken(storedToken);
      setUserId(storedUserId);
    }
    setAuthLoading(false); // done reading — PrivateRoute can now decide
  }, []); // Empty dependency array to run this once on mount

  const login = (email: string, token: string, claim: string, userId?: string) => {
    // Store the user info and claims in localStorage
    localStorage.setItem('email', email);
    localStorage.setItem('token', token);
    localStorage.setItem('defaultClaim', claim);
    if (userId) {
      localStorage.setItem('userId', userId);
    }

    // Update the state
    setIsAuthenticated(true);
    setUserClaims([claim]);
    setToken(token);
    setUserId(userId || null);

    // Track login activity (async, don't wait for it)
    if (userId) {
      trackLoginActivity(userId, token);
    }
  };

  const logout = () => {
    const curUserId = localStorage.getItem('userId');
    const curToken = localStorage.getItem('token');

    // Track logout activity before clearing (async, don't wait for it)
    if (curUserId && curToken) {
      trackLogoutActivity(curUserId, curToken);
    }

    // Clear the localStorage
    localStorage.removeItem('email');
    localStorage.removeItem('token');
    localStorage.removeItem('defaultClaim');
    localStorage.removeItem('userId');

    // Reset the state
    setIsAuthenticated(false);
    setUserClaims([]);
    setToken(null);
    setUserId(null);

    // Force a full navigation so dashboard UI is cleared immediately
    if (typeof window !== 'undefined') {
      window.location.replace('/');
    }
  };

  const trackLoginActivity = async (userId: string, token: string) => {
    try {
      await axios.post(
        `${ENDPOINT}/activity/track/login`,
        {
          user_id: userId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      console.error('Error tracking login:', error);
    }
  };

  const trackLogoutActivity = async (userId: string, token: string) => {
    try {
      await axios.post(
        `${ENDPOINT}/activity/track/logout`,
        {
          user_id: userId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      console.error('Error tracking logout:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, authLoading, userClaims, token, userId, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
